import { Component, OnInit, ViewChild } from '@angular/core';
import { SkillConstants } from '../../constants/skill-constants';
import { ControlType, SearchControl } from 'bpt-ui-library/bpt-search';
import { SkillSearchService } from '../../services/skill-search.service';
import { SkillPersistenceService } from '../../services/skill-persistence.service';
import { MessageService } from 'primeng/api';
import { SkillQueryResult } from '../../models/skill-search-result';
import { Observable, of } from 'rxjs';
import { take, switchMap, map, catchError, tap } from 'rxjs/operators';
import { SkillService } from '../../services/skill.service';
import { SearchMapperService } from '../../../shared/services/search-mapper.service';
import { EqmsScopeManagementService } from '../../../shared/services/eqms-scope-management.service';
import { PanelHubComponent } from '../../../shared-components/panel-hub/panel-hub.component';
import { PanelHubConfiguration } from '../../../shared-components/panel-hub/models/panel-hub-configuration';
import { PanelHubStateChangedEvent } from '../../../shared-components/panel-hub/models/panel-hub-state-changed-event';
import { PanelHubEventType } from '../../../shared-components/panel-hub/enums/panel-hub-eventtype';
import { Features } from '../../../shared-components/panel-hub/enums/features.enum';
import { PaginationConfigByFeature } from '../../../shared-components/panel-hub/constants/feature-pagination-configuration';
import { PanelHubConstants } from '../../../shared-components/panel-hub/constants/panel-hub-constants';
import { PaginationState } from '../../../shared-components/panel-hub/models/pagination-state';
import { AppliedFilters } from '../../../shared-components/panel-hub/models/applied-filters';
import { SortCriteria } from '../../../shared-components/panel-hub/models/sort-criteria';
import { FilterConfig } from '../../../shared-components/panel-hub/models/filter-config';
import { SortField } from '../../../shared-components/panel-hub/models/sort-field';

@Component({
  selector: 'app-skill-search',
  templateUrl: './skill-search.component.html',
  styleUrls: ['./skill-search.component.scss']
})
export class SkillSearchComponent implements OnInit {
  searchControls: SearchControl[] = [];
  skills: any[] = [];
  scopes: Array<{ id: number; name: string }> = [];
  masterScopesList: Array<{ value: number; name: string }> = [];
  primarySearchText: string | null = null;
  filterData: any[] = [];
  baseFilters: any = null;
  hasUserSearched: boolean = false;
  secondarySearchText: string | null = null;
  @ViewChild(PanelHubComponent)
  private panelHub!: PanelHubComponent;

  panelEventHub: any;
  panelHubConfig: PanelHubConfiguration = {
    title: "",
    feature: Features.SkillsSearchView,
    standardFilterCount: PanelHubConstants.StandardFilterLength,
    showSelectAll: false,
    enableShowAllToggle: false,
    enableSelectAll: false,
    enableSelection: false,
    enableToggling: true,
    enablePanelCardClick: false,
    enablePanelHighLight: false,
    enablePanelCardView: false,
    enableExpandCollapse: true,
    enableViewModes: false,
    paginationConfig: PaginationConfigByFeature[Features.SkillsSearchView],
  };

  skillPagedResult: SkillQueryResult = { totalRecords: 0, data: [], pageNumber: 1, pageSize: 0 };
  pagedData: SkillQueryResult = { totalRecords: 0, data: [], pageNumber: 1, pageSize: 0 };
  currentFilters: AppliedFilters | null = null;
  currentPagination: PaginationState | null = null;
  currentSort: SortCriteria | null = null;
  isPanelHubDataAvailable: boolean = false;
  noData: boolean = false;
  isMasterDataLoaded = false;

  constructor(
    private skillSearchService: SkillSearchService,
    private skillService: SkillService,
    private searchMapperService: SearchMapperService,
    private eqmsScopeManagementService: EqmsScopeManagementService,
    private messageService: MessageService,
    private skillPersistenceService: SkillPersistenceService
  ) { }

  ngOnInit(): void {
    this.initializeSearchControls();
    this.loadScopesAndConfigurePanel();
  }

  openSkill(skillCode: string | undefined | null): void {
    if (!skillCode) return;
    this.skillPersistenceService.openSkillDetails(skillCode);
  }

  onPanelEventHubInitialized(event: any) {
    this.panelEventHub = event;
  }

  private loadScopesAndConfigurePanel() {
    this.skillService.getScopes().pipe(take(1)).subscribe({
      next: (scopes: any[]) => {
        const scopesList = (scopes ?? []).map((scope: any) => ({
          id: scope.eQMSScopeId,
          name: scope.eQMSScopeName
        }));
        this.scopes = scopesList;
        const scopeOptions = scopesList.map((scope: any) => ({ value: scope.id, name: scope.name }));
        this.searchControls = this.searchControls.map(c => c.fieldName === 'eQMSScope' ? { ...c, options: scopeOptions } : c);
        const filterConfigs = this.buildFilterConfigs();
        const sortOptions = this.buildSortOptions();
        this.setPanelHubConfigExtensions(filterConfigs, sortOptions);
      },
      error: () => {
        this.messageService.add({ severity: SkillConstants.Severity_Err, summary: SkillConstants.SKILL_SEARCH_SCOPE_LOAD_ERROR });
      },
      complete: () => {
        this.isMasterDataLoaded = true;
        this.currentPagination = {
          pageNumber: this.panelHubConfig.paginationConfig.startPage,
          pageSize: this.panelHubConfig.paginationConfig.defaultPageSize,
          totalRecords: 0
        };
      }
    });
  }
  private initializeSearchControls() {
    this.searchControls = [
      {
        controlType: ControlType.List,
        label: 'eQMS Scope(s)',
        fieldName: 'eQMSScope',
        multiSelect: true,
        options: [],
        value: undefined,
        labelField: 'name',
        valueField: 'value',
        showFilter: true,
        order: 1,
      },
      {
        controlType: ControlType.List,
        label: 'Status',
        fieldName: 'status',
        multiSelect: true,
        options: [
          { value: true, name: 'Active' },
          { value: false, name: 'Inactive' }
        ],
        value: undefined,
        labelField: 'name',
        valueField: 'value',
        showFilter: true,
        order: 2,
      }
    ];
  }

  private setPanelHubConfigExtensions(filterConfigs: FilterConfig[], sortOptions: SortField[]) {
    this.panelHubConfig = { ...this.panelHubConfig, filterConfigs, sortOptions };
  }

  getSearchResults = (searchText: string): Observable<number> => {
    this.skills = [];
    this.filterData = [];
    this.baseFilters = null;
    this.primarySearchText = (searchText !== null && searchText !== undefined) ? searchText : null;
    this.hasUserSearched = true;
    this.resetPanelConfigurationAfterSearch();
    return this.performSearch();
  };

  getAdvancedSearchResults = (filters: Array<any>, searchText: string): Observable<number> => {
    this.filterData = filters;
    this.skills = [];
    this.primarySearchText = (searchText !== null && searchText !== undefined) ? searchText : null;
    this.hasUserSearched = true;
    this.resetPanelConfigurationAfterSearch();
    return this.performSearch();
  };

  private performSearch(): Observable<number> {
    if (!this.currentPagination) {
      this.currentPagination = {
        pageNumber: this.panelHubConfig.paginationConfig.startPage,
        pageSize: this.panelHubConfig.paginationConfig.defaultPageSize,
        totalRecords: 0
      };
    }
    const advancedSearchFilters = this.getAdvancedSearchFilters();
    if (this.areAllValuesNullOrUndefined({ scopeIds: advancedSearchFilters.scopeIds, source: advancedSearchFilters.isActive, searchText: this.primarySearchText, statusProvided: advancedSearchFilters.statusProvided })) {
      this.messageService.add({ severity: SkillConstants.Severity_Info, summary: SkillConstants.SKILL_SEARCH_FILTER_REQUIRED_INFO });
      return of(0);
    }

    const panelFilters = this.searchMapperService.mapToBaseSearchRequest(this.currentFilters, this.currentSort, this.currentPagination);
    const payload: any = {
      ...panelFilters,
      PrimarySearchText: this.primarySearchText || null,
      Scopes: advancedSearchFilters.scopeIds && advancedSearchFilters.scopeIds.length > 0 ? advancedSearchFilters.scopeIds : null,
      Status: (advancedSearchFilters.isActive === undefined || advancedSearchFilters.isActive === null) ? null : advancedSearchFilters.isActive,
    };

    this.panelEventHub?.startLoader?.();
    this.ensureMasterScopesLoaded().pipe(
      switchMap(() => this.skillSearchService.searchSkillsPaged(payload))
    ).subscribe({
      next: (resp: SkillQueryResult) => {
        const processed = this.transformSkills(resp.data);
        this.skills = processed;
        this.skillPagedResult = {
          data: processed,
          totalRecords: resp.totalRecords,
          pageNumber: resp.pageNumber,
          pageSize: resp.pageSize
        };
        this.pagedData = { ...this.skillPagedResult };
        if (this.currentPagination) this.currentPagination.totalRecords = resp.totalRecords;
        this.noData = resp.data.length === 0;
        this.isPanelHubDataAvailable = true;
      },
      error: () => {
        this.messageService.add({ severity: SkillConstants.Severity_Err, summary: SkillConstants.SKILL_SEARCH_RESULTS_LOAD_ERROR });
        this.panelEventHub?.stopLoader?.();
      },
      complete: () => {
        this.panelEventHub?.stopLoader?.();
      }
    });
    return of(this.skillPagedResult.totalRecords);
  }

  onPageChange(newPage: number) {
    if (!this.currentPagination) return;
    this.currentPagination.pageNumber = newPage;
    this.performSearch();
  }
  onPanelHubEvent(event: PanelHubStateChangedEvent) {
    if (!this.hasUserSearched) return;
    switch (event.type) {
      case PanelHubEventType.FilterChanged:
        this.secondarySearchText = event.state.filters?.searchText || null;
        this.currentFilters = event.state.filters || null;
        this.refreshPanelHubData();
        break;
      case PanelHubEventType.SortChanged:
        this.currentSort = event.state.sort || null;
        this.refreshPanelHubData();
        break;
      case PanelHubEventType.PaginationChanged:
        this.currentPagination = event.state.pagination || null;
        this.refreshPanelHubData();
        break;
    }
  }

  private refreshPanelHubData() {
    this.performSearch();
  }

  private transformSkills(list: any[] | null | undefined): any[] {
    if (!Array.isArray(list) || list.length === 0) return [];
    return list.map(s => this.enrichSkill(s));
  }

  private enrichSkill(skill: any): any {
    let scopeDisplayNames: string[] | undefined = undefined;
    const masterLookupAvailable = (this.masterScopesList && this.masterScopesList.length > 0);
    if (skill && Array.isArray(skill.eQMSScopes) && skill.eQMSScopes.length > 0) {
      if (masterLookupAvailable) {
        scopeDisplayNames = skill.eQMSScopes
          .map((scopeId: any) => {
            const scope = this.masterScopesList.find((s: any) => String(s.value) === String(scopeId));
            return scope ? scope.name : null;
          })
          .filter((name: string | null): name is string => name !== null);
      }
    }
    return {
      ...skill,
      scopeDisplayNames: scopeDisplayNames || skill.scopeDisplayNames || []
    };
  }

  private resetPanelConfigurationAfterSearch() {
    this.currentPagination = {
      pageNumber: this.panelHubConfig.paginationConfig.startPage,
      pageSize: this.panelHubConfig.paginationConfig.defaultPageSize,
      totalRecords: 0
    };
    this.currentFilters = null;
    this.currentSort = null;
    this.secondarySearchText = null;
    if (this.panelEventHub?.resetConfigurations) this.panelEventHub.resetConfigurations();
  }


  private buildFilterConfigs(): FilterConfig[] {
    const scopeOptions = (this.scopes || []).map((s: any) => ({ name: s.name, value: s.id }));
    return [
      { displayName: 'eQMS Scope(s)', fieldName: 'scopes', options: scopeOptions },
      { displayName: 'Status', fieldName: 'status', options: [{ name: 'Active', value: true }, { name: 'Inactive', value: false }] }
    ].filter(fc => fc.options && fc.options.length > 0);
  }

  private buildSortOptions(): SortField[] {
    return [
      { fieldDisplayName: 'Name', fieldName: 'name' },
      { fieldDisplayName: 'eQMS Scope(s)', fieldName: 'scopes' },
      { fieldDisplayName: 'Status', fieldName: 'status' }
    ];
  }

  private areAllValuesNullOrUndefined(data: any): boolean {
    if (!data) return true;
    const { scopeIds, source, searchText, statusProvided } = data;
    const isScopeEmpty = !scopeIds || (Array.isArray(scopeIds) && scopeIds.length === 0);
    const isActiveEmpty = (!statusProvided) && (source === null || source === undefined);
    const isSearchEmpty = !searchText || (typeof searchText === 'string' && searchText.trim() === '');
    return isScopeEmpty && isActiveEmpty && isSearchEmpty;
  }

  private getAdvancedSearchFilters() {
    let scopeIds: number[] | undefined;
    let isActive: boolean | null = null;
    let statusProvided = false;

    if (this.filterData && this.filterData.length > 0) {
      const scopeFilter = this.filterData.find(f => f.columnName === 'eQMSScope');
      if (scopeFilter) { scopeIds = scopeFilter.values; }
      const activeFilter = this.filterData.find(f => f.columnName === 'status');
      if (activeFilter) {
        statusProvided = Array.isArray(activeFilter.values) ? activeFilter.values.length > 0 : !!activeFilter.values;
        const selectedValues = Array.isArray(activeFilter.values) ? activeFilter.values : [activeFilter.values];
        if (!selectedValues || selectedValues.length === 0) {
          isActive = null;
        } else {
          const hasActive = selectedValues.includes(true);
          const hasInactive = selectedValues.includes(false);
          if (hasActive && hasInactive) {
            isActive = null;
          } else if (hasActive) {
            isActive = true;
          } else if (hasInactive) {
            isActive = false;
          }
        }
      }
    }

    return { scopeIds, isActive, statusProvided };
  }

  private ensureMasterScopesLoaded(): Observable<void> {
    if (this.masterScopesList && this.masterScopesList.length > 0) return of(void 0);
    return this.eqmsScopeManagementService.geteQMSScopeLookup().pipe(
      take(1),
      map((lookup: any[]) => {
        this.masterScopesList = (lookup ?? []).map((s: any) => ({ value: s.eQMSScopeId, name: s.eQMSScopeName }));
        return;
      }),
      catchError(() => {
        this.messageService.add({ severity: SkillConstants.Severity_Err, summary: SkillConstants.SKILL_SEARCH_MASTER_SCOPE_LOAD_ERROR });
        return of(void 0);
      })
    );
  }

}
